import { InlineError, LoadingIndicator } from "@/components/QueryState";
import { api } from "@/lib/api";
import { modelName } from "@/lib/models";
import { useQuery } from "@tanstack/react-query";

// Super-admin, read-only: what this org set up on its Models page. Owners and
// admins change it there; nothing here switches anything on or off.
export function OrgModelsPanel({ orgId }: { orgId: string }) {
  const query = useQuery({ queryKey: ["admin-model-policy", orgId], queryFn: () => api.admin.modelPolicy(orgId) });
  if (query.isLoading) return <LoadingIndicator />;
  if (query.isError || !query.data) return <InlineError error={query.error ?? "Failed to load"} />;
  const p = query.data;
  const defaults = Object.entries(p.defaults);

  return (
    <div className="max-w-2xl space-y-6 text-sm">
      <p className="text-muted-foreground">
        Read-only. The organization&apos;s owners and admins manage this on their Models page.
      </p>

      <section className="space-y-2">
        <h3 className="font-medium">Defaults</h3>
        {defaults.length === 0 ? (
          <p className="text-muted-foreground">None set. Boos run their built-in models.</p>
        ) : (
          <dl className="grid grid-cols-[10rem_1fr] gap-x-4 gap-y-1">
            {defaults.map(([purpose, slug]) => (
              <div key={purpose} className="contents">
                <dt className="text-muted-foreground">{purpose}</dt>
                <dd className="font-mono text-xs leading-5">{slug}</dd>
              </div>
            ))}
          </dl>
        )}
      </section>

      <section className="space-y-2">
        <h3 className="font-medium">Turned off ({p.disabledModels.length})</h3>
        {p.disabledModels.length === 0 ? (
          <p className="text-muted-foreground">Every model is on.</p>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {p.disabledModels.map((slug) => (
              <li key={slug} className="rounded border px-1.5 font-mono text-xs" title={modelName(slug)}>
                {slug}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <h3 className="font-medium">Own endpoints ({p.endpoints.length})</h3>
        {p.endpoints.length === 0 ? (
          <p className="text-muted-foreground">None.</p>
        ) : (
          <ul className="divide-y rounded-lg border">
            {p.endpoints.map((e) => (
              <li key={e.name} className="space-y-1 px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{e.name}</span>
                  <span className="text-xs text-muted-foreground">{e.hasKey ? "Key saved" : "No key"}</span>
                </div>
                <p className="truncate font-mono text-xs text-muted-foreground">{e.baseUrl}</p>
                <p className="font-mono text-xs">{e.models.map((m) => `${m.id} (${m.role})`).join(", ")}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
